/**
 * Dynamic Cart Drawer Upsells - Priority-Based
 *
 * Shows 2 upsell products based on priority order (by sales volume).
 * Automatically filters out products already in cart.
 */

class DynamicUpsells {
  constructor() {
    this.upsellContainer = null;
    this.currentUpsellHandles = [];
    this.cartDrawerObserver = null;
    this.refreshFrame = null;
    this.isRenderingUpsells = false;

    // Priority list of upsell products (by sales volume)
    this.PRIORITY_LIST = [
      'deskguard-glass-desk-mats',  // DeskGuard
      'glass-surface-cleaner',      // Repel
      'leather-felt-desk-pad',      // Leather Desk Pad
      'leather-footrest',           // Footrest
      'wood-monitor-riser',         // Monitor Riser
      'leather-valet-tray',         // Valet Tray
      'wood-headphone-stand',       // Headphone Stand
      'leather-mouse-pad',          // MousePad
      'mod-mop'                     // Mop
    ];

    // Never show this product
    this.EXCLUDED_HANDLE = 'hubless-office-chair-wheels';

    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Impact owns cart drawer rendering. Do not render upsells directly on
    // variant:add/cart:change because those events can fire while the drawer
    // DOM is still being replaced.

    document.addEventListener('dialog:before-show', (event) => this.handleDialogOpen(event));

    this.setupCartDrawerObserver();
    this.loadUpsellsOnPageLoad();
  }

  /**
   * Watch the cart drawer for child-list changes.
   * Impact replaces portions of the drawer after cart changes, so refresh
   * upsells only after that DOM update has landed.
   */
  setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('#cart-drawer');

    if (!cartDrawer) {
      return;
    }

    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }

    this.cartDrawerObserver = new MutationObserver((mutations) => {
      // Ignore mutations caused by our own upsell render.
      if (this.isRenderingUpsells) {
        return;
      }

      const drawerChanged = mutations.some((mutation) =>
        mutation.type === 'childList' && mutation.addedNodes.length > 0
      );

      if (drawerChanged) {
        this.scheduleUpsellRefresh();
      }
    });

    this.cartDrawerObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Debounce drawer replacements and refresh on the next frame.
   */
  scheduleUpsellRefresh() {
    if (this.refreshFrame) {
      cancelAnimationFrame(this.refreshFrame);
    }

    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = null;
      this.refreshUpsellsAfterDOMUpdate();
    });
  }

  /**
   * Refresh upsells after the cart drawer DOM has been updated.
   * Always rebuild from the current cart into the current drawer container.
   */
  async refreshUpsellsAfterDOMUpdate() {
    this.upsellContainer = null;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart.item_count) {
        this.hideUpsells();
        return;
      }

      const upsellProducts = await this.determineUpsells(cart);

      if (upsellProducts.length > 0) {
        this.renderUpsells(upsellProducts, false);
      } else {
        this.hideUpsells();
      }
    } catch (error) {
      console.error('[Dynamic Upsell] Refresh failed:', error);
    }
  }

  /**
   * Load upsells on page load if cart already has items.
   */
  async loadUpsellsOnPageLoad() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.item_count > 0) {
        const upsellProducts = await this.determineUpsells(cart);

        if (upsellProducts.length > 0) {
          this.renderUpsells(upsellProducts);
        } else {
          this.hideUpsells();
        }
      }
    } catch (error) {
      console.error('[Dynamic Upsell] Initial load failed:', error);
    }
  }

  /**
   * Handle when cart drawer opens.
   * @param {CustomEvent} event - dialog:before-show event
   */
  async handleDialogOpen(event) {
    const target = event.target;

    if (!target || target.id !== 'cart-drawer') {
      return;
    }

    if (!this.cartDrawerObserver) {
      this.setupCartDrawerObserver();
    }

    this.upsellContainer = null;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart.item_count) {
        this.hideUpsells();
        return;
      }

      const upsellProducts = await this.determineUpsells(cart);

      if (upsellProducts.length > 0) {
        this.renderUpsells(upsellProducts);
      } else {
        this.hideUpsells();
      }
    } catch (error) {
      console.error('[Dynamic Upsell] Drawer-open refresh failed:', error);
    }
  }

  /**
   * Get the upsell container.
   */
  getUpsellContainer() {
    if (!this.upsellContainer || !document.documentElement.contains(this.upsellContainer)) {
      this.upsellContainer = document.querySelector('[data-dynamic-upsell-container]');
    }

    return this.upsellContainer;
  }

  /**
   * Determine which 2 upsell products to show based on priority list.
   * @param {Object} cart - The full cart object
   * @returns {Promise<Array>} - Array of up to 2 product objects
   */
  getGlassClarityFromCart(cart) {
    let clarity = null;

    cart.items.forEach(item => {
      const handle = this.getProductHandle(item.handle || item.url);
      const text = `${item.product_title || ''} ${item.variant_title || ''}`.toLowerCase();

      const isChairMat =
        handle.includes('glass-chair-mat') ||
        text.includes('glass chair mat');

      if (!isChairMat) return;

      if (text.includes('chiaro')) {
        clarity = 'Chiaro';
      } else if (text.includes('luminoso')) {
        clarity = 'Luminoso';
      }
    });

    return clarity;
  }

  async determineUpsells(cart) {
    const cartHandles = cart.items.map(item => this.getProductHandle(item.handle || item.url));
    const availableProducts = [];

    this.deskGuardClarity = this.getGlassClarityFromCart(cart);

    for (const handle of this.PRIORITY_LIST) {
      if (cartHandles.includes(handle)) continue;
      if (handle === this.EXCLUDED_HANDLE) continue;

      const product = await this.fetchProductDetails(handle);

      if (product) {
        availableProducts.push(product);

        if (availableProducts.length === 2) {
          break;
        }
      }
    }

    return availableProducts;
  }

  /**
   * Fetch full product details by handle.
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>}
   */
  async fetchProductDetails(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Extract product handle from URL or handle string.
   * @param {string} handleOrUrl - Handle or URL
   * @returns {string}
   */
  getProductHandle(handleOrUrl) {
    if (!handleOrUrl) return '';

    if (handleOrUrl.includes('/products/')) {
      const match = handleOrUrl.match(/\/products\/([^/?]+)/);
      return match ? match[1] : handleOrUrl;
    }

    return handleOrUrl;
  }

  /**
   * Render the upsell products in the container.
   * @param {Array} products - Array of 1-2 product objects
   * @param {boolean} animate - Whether to apply animation classes
   */
  renderUpsells(products, animate = true) {
    const container = this.getUpsellContainer();

    if (!container || !products || products.length === 0) {
      return;
    }

    this.currentUpsellHandles = products.map(p => p.handle);
    this.isRenderingUpsells = true;

    container.style.display = 'block';
    container.innerHTML = this.createUpsellsHTML(products, animate);

    requestAnimationFrame(() => {
      this.isRenderingUpsells = false;
    });
  }

  /**
   * Create HTML for upsell products.
   * @param {Array} products - Array of product objects
   * @param {boolean} animate - Whether to include animation classes
   * @returns {string}
   */
  createUpsellsHTML(products, animate = true) {
    const productItems = products.map(product => this.createProductItemHTML(product)).join('');
    const animationClass = animate ? '' : ' no-animation';

    return `
      <div class="dynamic-upsell-container${animationClass}">
        <h2 class="dynamic-upsell-heading">You May Like These Too</h2>
        <div class="dynamic-upsell-grid">
          ${productItems}
        </div>
      </div>
    `;
  }

  /**
   * Create HTML for a single product item.
   * @param {Object} product - Product data
   * @returns {string}
   */
  createProductItemHTML(product) {
    let variant = null;

    if (
      product.handle === 'deskguard-glass-desk-mats' &&
      this.deskGuardClarity &&
      product.variants &&
      product.variants.length > 0
    ) {
      const clarityLower = this.deskGuardClarity.toLowerCase();

      variant =
        product.variants.find(v => {
          const text = `${v.public_title || v.title || ''}`.toLowerCase();
          return text.includes(clarityLower);
        }) ||
        product.variants.find(v => v.available) ||
        product.variants[0];

      product.title = variant.option1 + ' - ' + product.title;
    } else if (product.variants && product.variants.length > 0) {
      variant = product.variants.find(v => v.available) || product.variants[0];
    }

    if (!variant) {
      return '';
    }

    const price = this.formatMoney(variant.price || product.price || 0);
    const image = product.images?.[0] || product.image || product.featured_image || '';

    return `
      <div class="dynamic-upsell-item">
        <div class="dynamic-upsell-item__image-wrapper">
          ${image ? `<img src="${image}" alt="${product.title}" loading="lazy" />` : ''}
        </div>
        <div class="dynamic-upsell-item-content">
          <p class="dynamic-upsell-item_title">${product.title}</p>
          <p class="dynamic-upsell-item__price">${price} USD</p>
        </div>
        <button
          class="dynamic-upsell-item__button"
          data-variant-id="${variant.id}"
          data-variant-sku="${variant.sku}"
          ${!variant.available ? 'disabled' : ''}
        >
          ${variant.available ? 'ADD' : 'SOLD OUT'}
        </button>
      </div>
    `;
  }

  /**
   * Format money using simple dollar formatting.
   * @param {number} cents - Price in cents
   * @returns {string}
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    return `$ ${dollars.toFixed(2)}`;
  }

  /**
   * Hide the upsell container.
   */
  hideUpsells() {
    const container = this.getUpsellContainer();

    if (container) {
      this.isRenderingUpsells = true;
      container.style.display = 'none';
      container.innerHTML = '';
      this.currentUpsellHandles = [];

      requestAnimationFrame(() => {
        this.isRenderingUpsells = false;
      });
    }
  }
}

if (typeof window !== 'undefined') {
  window.DynamicUpsells = new DynamicUpsells();
}
